/**
 * boardTools.ts — LINE ページの「ツール」メニューの操作（純関数・2026-10-26 booking-platform/md/customer-chat.md §13-6）
 *
 * 最初の操作「ピンを外す（過去返却分）」: ピン留めした LINE と予約チャットのうち、返却日が昨日以前のものを選ぶ。
 * - LINE はピンがユーザー単位なので、紐づいた予約（スタッフチャット STAFF: は除く）が **すべて** 昨日以前に返却のユーザーだけ。
 *   今日以降に返却の予約が 1 件でもある・予約データが見つからない予約がある、なら外さない（判断できないものは残す）
 * - 予約チャットは会話ごと（1 予約 1 会話）。匿名チャットは予約が無いので対象外
 */
import { chatKindOf } from './lpChatBoard';

/** ツールメニューの項目（今後ここに足す） */
export const BOARD_TOOLS: ReadonlyArray<{ id: string; label: string; description: string }> = [
  { id: 'unpin_past_returns', label: 'ピンを外す（過去返却分）', description: '返却日が昨日以前の予約のピン留めをまとめて外す' },
];

export interface PastReturnPin {
  /** 一覧の中で一意（LINE は line:<userId>、予約チャットは resv:<session_key>） */
  key: string;
  kind: 'line' | 'resv';
  line_user_id?: string;
  session_key?: string;
  name: string;
  /** いちばん遅い返却日（YYYY-MM-DD） */
  lastReturnDate: string;
}

const dateOnly = (v: unknown): string => (typeof v === 'string' ? v.split(' ')[0].split('T')[0] : '');

/**
 * @param instances LineChatBoard の chatInstances（LINE は予約ごとに展開された行・自社チャットは会話ごと）
 * @param findReservation 予約 ID → 予約データ（reservationSyncService）。無ければ undefined
 * @param todayStr 'YYYY-MM-DD'（この日より前＝昨日以前が対象）
 */
export function findPastReturnPins(
  instances: Record<string, any>[],
  findReservation: (id: string) => { end_date?: string | null; return_date?: string | null; customer_name?: string | null } | undefined,
  todayStr: string,
): PastReturnPin[] {
  const out: PastReturnPin[] = [];
  const seenLine = new Set<string>();
  for (const i of instances) {
    if (!i || !i.is_pinned) continue;
    const kind = chatKindOf(i);
    if (kind === 'anon') continue;
    if (kind === 'line') {
      const uid = String(i.line_user_id || '');
      if (!uid || seenLine.has(uid)) continue;
      seenLine.add(uid);
      const ids: string[] = (i.reservation_ids || []).map((x: unknown) => String(x)).filter((x: string) => x && !x.startsWith('STAFF:'));
      if (ids.length === 0) continue;
      let latest = '';
      let ok = true;
      for (const id of ids) {
        const r = findReservation(id);
        const end = dateOnly(r?.end_date || r?.return_date);
        if (!end || end >= todayStr) { ok = false; break; }
        if (end > latest) latest = end;
      }
      if (!ok) continue;
      out.push({ key: `line:${uid}`, kind: 'line', line_user_id: uid, name: i.custom_display_name || i.line_display_name || uid, lastReturnDate: latest });
      continue;
    }
    // 予約チャット
    const rid = i.instance_reservation_id != null ? String(i.instance_reservation_id) : '';
    if (!rid || !i.session_key) continue;
    const r = i.instance_res_data || findReservation(rid);
    const end = dateOnly(r?.end_date || r?.return_date);
    if (!end || end >= todayStr) continue;
    out.push({ key: `resv:${i.session_key}`, kind: 'resv', session_key: i.session_key, name: i.custom_display_name || i.line_display_name || rid, lastReturnDate: end });
  }
  return out.sort((a, b) => (a.lastReturnDate < b.lastReturnDate ? -1 : a.lastReturnDate > b.lastReturnDate ? 1 : 0));
}
